
import { useI18n } from "src/utils/lni18n";
import { useDialog } from "src/components/Modalservice/Dialogservice";

import { PermissionsService } from "src/components/Permissions/PermissionService";
import { usePermissionsService } from "src/components/Permissions/usePermissionsService";
import PermissionsForm from "src/components/Permissions/PermissionForm";
import { DialogData } from "src/components/Permissions/PermissionTypes";
import { ObjectTypes } from "src/coursepart/domain/ObjectTypes";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import courseService, { currentCourseAtom, oneCourseatom } from "./CourseService";
import LnIcon from "src/components/LnIcon";
import { useNavigate } from "react-router-dom";
import HtmlTextEditor from "src/components/TextEditor/HtmlTextEditor";
import { debouncer } from "src/utils/debouncer";
import { coursePermissionsUrlCreator } from "./CoursePermissionsUrlCreator";
import { CopyCourseDialog, CopyCourseDialogOptions } from "./CopyCourseDialog";
import { CourseCopyCommand } from "./domain/CourseCopyCommand";
import { urlService } from "src/services/NavService";
import { AlertDialog } from "src/components/Modalservice/AlertDialog";
import WaitRipple from "src/components/WaitRipple";
import { useAddTheme } from "./UseAddTheme";
import { useConfirm } from "src/components/Modalservice/UseConfirm";
import { isValidName, nameTooShortText } from "src/utils/NameRules";

const CourseGeneral = () => {

    const dialog = useRef<HTMLDialogElement | null>(null);

    const [courseId] = useAtom(currentCourseAtom);
    const saveDescDebounce = useRef<debouncer | undefined>(undefined);
    const saveNameDebounce = useRef<debouncer | undefined>(undefined);
    const courseAtom = useMemo(() => oneCourseatom(courseId || ""), [courseId]);
    const [courseData] = useAtom(courseAtom);
    const confirm = useConfirm();
    const { languageService: t } = useI18n();
    const dialogPortal = useDialog();
    const navigate = useNavigate();

    const addTheme = useAddTheme();

    const [initialData, setInitialData] = useState<{ name: string, desc: string }>({ name: "", desc: "" });
    const [nameInvalid, setNameInvalid] = useState(false);

    useEffect(() => {
        if (courseData && courseData.Course) {
            setInitialData({ desc: courseData.Course.Description, name: courseData.Course.Name });
        }
    }, [courseId]);


    useEffect(() => {
        saveDescDebounce.current = new debouncer(courseService.saveCourseDescription, 500, courseService);
        saveNameDebounce.current = new debouncer(courseService.saveCourseName, 500, courseService);
        return () => saveDescDebounce.current?.flush();
    }, [])


    const permissionCourseId = courseData?.UseCoursePermissionsTable ? courseData.Course.Id : null;
    const permissionService = usePermissionsService(permissionCourseId, id =>
        new PermissionsService(id,
            coursePermissionsUrlCreator.GetstartUrl(id, ObjectTypes.course),
            coursePermissionsUrlCreator.GetSearchUrl(ObjectTypes.course, id),
            () => { },
            (a: any) => { }
        ));

    const permissionShowData: DialogData = useMemo(() => ({
        objectId: courseId!,
        objType: ObjectTypes.course,
        setForallSubitems: false,
        setFlags: []
    }), [courseId])



    const copyCourse = useCallback(() => {
        dialogPortal({
            factory: (onSubmit, onCancel) => {
                const dprops: CopyCourseDialogOptions = {
                    languageService: t,
                    onCancel: onCancel,
                    onSubmit: onSubmit,
                    course: courseData.Course
                }
                return <CopyCourseDialog {...dprops} />
            },
            size: "xl"
        }).then(async (res: CourseCopyCommand) => {
            copyCourseWait(res);
        }).catch((e: Error) => {});
    }, [courseData]);


    const copyCourseWait = (result: CourseCopyCommand) => {
        dialog.current?.showModal();
        result.DeepPublish = false; // could be otional, but need some work on API first....
        return courseService.copyCourse(result).then(id => {
            if (id) {
                navigate(urlService.urlToCourse(id));
            }
        }).catch((e: Error) => { })
            .finally(() => {
                dialog.current?.close();
            });
    }


    function deleteCourse(): void {

        confirm("md", t.getText("delete"),
                <div className="preserve-white" >{t.getText("delete_course_confirm")}</div>)
            .then(async (res: boolean) => {
            if (res) {
                const res = await courseService.deleteCourse(courseId!);
                if (res) {
                    navigate(urlService.urlToCourse(res));
                }
                else {
                    navigate(`/`);
                }
            }
        }).catch((e: Error) => { });
    }


    function saveDescription(e: ChangeEvent<HTMLTextAreaElement>): void {
        const { value } = e.currentTarget;
        saveDescDebounce.current && saveDescDebounce.current.debounce(courseId!, value);
    }

    function saveName(e: ChangeEvent<HTMLInputElement>) {
        const { value } = e.currentTarget;
        const valid = isValidName(value);
        setNameInvalid(!valid);
        if (valid) {
            saveNameDebounce.current && saveNameDebounce.current.debounce(courseId!, value);
        }
    }

    // A name that is still too short when the field is left goes back to the last saved one.
    function restoreName(e: React.FocusEvent<HTMLInputElement>) {
        if (!isValidName(e.currentTarget.value)) {
            e.currentTarget.value = courseData.Course.Name;
            setNameInvalid(false);
        }
    }


    if (!courseData || !courseData.Course) {
        return null;
    }

    const readOnly = !!courseData?.Locked;
    const haveClasses = courseData.Course.StudentClasses && courseData.Course.StudentClasses.length > 0;
    return (
        <>

            <div className="mb-5">
                <h3 className="mb-4">
                    {courseData.IsDirty && <LnIcon name="alert" className='icon-small me-3' />}
                    {t.getText("general")}
                </h3>
                <div className="row">
                    <div className="col-sm-12 col-md-8 position-relative mb-4">
                        <h5 className="mb-2">{t.getText("name")}</h5>
                        <input disabled={readOnly} type="text" defaultValue={initialData.name} className={`form-control${nameInvalid ? " is-invalid" : ""}`}
                            onChange={saveName} onBlur={restoreName} />
                        {nameInvalid && <div className="invalid-feedback">{nameTooShortText(t)}</div>}

                        <h5 className="mt-4 mb-2">{t.getText("description")}</h5>
                        <HtmlTextEditor readOnly={readOnly} onChange={saveDescription} initialValue={initialData.desc} name="Description"  ></HtmlTextEditor>

                    </div>

                    <div className="col-md-4  col-12 d-flex flex-column">
                        {!readOnly &&
                            <button onClick={()=>addTheme(courseId!)} className="ms-auto  mb-5 btn btn-primary">{t.getText("add.theme")}</button>
                        }
                        {courseData.CanCopy &&
                            <button onClick={copyCourse} className="ms-auto  mb-3 btn btn-primary">{t.getText("copy")}</button>
                        }
                        {!readOnly &&
                            <button disabled={haveClasses} onClick={deleteCourse} className="ms-auto mb-3 btn btn-warning">{t.getText("delete")}</button>
                        }
                        {haveClasses && <div className="ms-auto w-lg-50 alert alert-warning">{t.getText("course.in.use")}</div>

                        }

                    </div>

                </div>

                {courseData.UseCoursePermissionsTable && permissionService &&
                    <div className="mt-3">
                        <PermissionsForm readOnly={readOnly} permissionService={permissionService}
                            showData={permissionShowData} urlCreator={coursePermissionsUrlCreator} />
                    </div>
                }

            </div >

            <dialog ref={dialog} className="md">
                <AlertDialog
                    title={t.getText("copy")}
                    languageService={t}
                    message={<>{t.getText("wait.moment")}<WaitRipple /></>}
                    className=""
                    onSubmit={(d) => { }}
                    noButton={true} />
            </dialog>
        </>
    )

}

export default CourseGeneral;
